package fr.frux.shared.data.local

import fr.frux.core.database.FruxDatabase
import fr.frux.core.maps.Location
import fr.frux.shared.domain.model.Site

/**
 * Cache local des lieux sur SQLDelight : boîte englobante dérivée du rayon puis haversine, comme
 * [SqlDelightTreeLocalDataSource]. [policy] fournit fraîcheur, expiration et plafond, [now]
 * l'horloge injectée (epoch millis).
 */
class SqlDelightSiteLocalDataSource(
    private val database: FruxDatabase,
    private val policy: TreeCachePolicy,
    private val now: () -> Long,
) : SiteLocalDataSource {
    private val queries get() = database.siteQueries

    override suspend fun nearbySites(
        lat: Double,
        lon: Double,
        radiusKm: Double,
    ): CachedSites {
        val box = CacheBounds.around(lat, lon, radiusKm)
        val center = Location(lat, lon)
        val rows =
            queries
                .selectInBounds(box.latMin, box.latMax, box.lngMin, box.lngMax, now() - policy.evictAfterMillis)
                .executeAsList()
        val plantings = plantingsBySite(rows.map { it.id })
        val matched =
            rows
                .mapNotNull { row ->
                    val distance = Location(row.latitude, row.longitude).distanceMetersTo(center)
                    if (distance > radiusKm * 1_000) return@mapNotNull null
                    row.toDomain(plantings[row.id].orEmpty())?.let { Triple(it, row.cached_at, distance) }
                }.sortedWith(compareBy({ it.third }, { it.first.id }))
        val isStale = matched.isNotEmpty() && matched.minOf { it.second } < now() - policy.staleAfterMillis
        return CachedSites(sites = matched.map { it.first }, isStale = isStale)
    }

    override suspend fun site(id: Long): Site? {
        val row =
            queries
                .selectById(id)
                .executeAsOneOrNull()
                ?.takeIf { it.cached_at >= now() - policy.evictAfterMillis }
        return row?.toDomain(plantingsBySite(listOf(id))[id].orEmpty())
    }

    override suspend fun upsertAll(sites: List<Site>) {
        if (sites.isEmpty()) return
        val cachedAt = now()
        queries.transaction {
            sites.forEach { site ->
                queries.upsertSite(
                    id = site.id,
                    name = site.name,
                    description = site.description,
                    latitude = site.latitude,
                    longitude = site.longitude,
                    access_rule = site.accessRule.name,
                    manager_name = site.managerName,
                    has_owner = if (site.hasOwner) 1L else 0L,
                    trees_count = site.treesCount.toLong(),
                    created_by = site.createdBy,
                    created_by_username = site.createdByUsername,
                    created_at = site.createdAtMillis,
                    cached_at = cachedAt,
                    next_harvest_id = site.nextHarvest?.id,
                    next_harvest_at = site.nextHarvest?.scheduledAtMillis,
                    next_harvest_hosted = site.nextHarvest?.let { if (it.hostedByOwner) 1L else 0L },
                )
                // Remplacement, pas fusion : une plantation retirée côté serveur doit disparaître.
                queries.deletePlantingsOf(site.id)
                site.plantings.forEach { planting ->
                    queries.insertPlanting(
                        id = planting.id,
                        site_id = site.id,
                        species_id = planting.species.id,
                        species_common_name_fr = planting.species.commonNameFr,
                        species_scientific_name = planting.species.scientificName,
                        species_form = planting.species.form.name,
                        count_estimate = planting.count.name,
                        note = planting.note,
                    )
                }
            }
        }
    }

    override suspend fun prune() {
        val threshold = now() - policy.evictAfterMillis
        queries.transaction {
            queries.deleteExpired(threshold)
            val overflow = queries.countAll().executeAsOne() - policy.maxSiteRows
            if (overflow > 0) queries.deleteOldestOverflow(overflow)
        }
    }

    override suspend fun delete(id: Long) {
        queries.deleteById(id)
    }

    private fun plantingsBySite(siteIds: List<Long>) =
        if (siteIds.isEmpty()) {
            emptyMap()
        } else {
            queries
                .selectPlantingsOf(siteIds)
                .executeAsList()
                .groupBy({ it.site_id }, { it.toDomain() })
                .mapValues { (_, list) -> list.filterNotNull() }
        }
}
